/**
 * modelLoader.ts
 *
 * Checks CacheStorage for the Vosk model on app boot.
 * If not present, downloads it using fetch and caches it.
 * This runs on the main thread but delegates heavy work to the Web Worker.
 */

// TODO: Vosk Model URL Configuration
// The Vosk speech recognition model must be hosted and accessible via HTTP.
// The model file (vosk-model-small-en-us-0.15) should be placed at the path below
// on the production server (https://tarot.mpmagic.co.uk/assets/).
//
// To prepare the model:
//   1. Download from: https://alphacephei.com/vosk/models/vosk-model-small-en-us-0.15.zip
//   2. Extract the archive — you'll get a folder named `vosk-model-small-en-us-0.15`
//   3. Re-archive it as a .tar.gz:  tar -czf vosk-model-small-en-us-0.15.tar.gz vosk-model-small-en-us-0.15/
//   4. Upload the .tar.gz to: https://tarot.mpmagic.co.uk/assets/
//
// Once the file is in place, uncomment the PRODUCTION URL below and comment out (or remove)
// the ORIGINAL URL pointing to alphacephei.com.
//
// ORIGINAL URL (direct download from the model author — use for development/testing only):
// const VOSK_MODEL_URL = 'https://alphacephei.com/vosk/models/vosk-model-small-en-us-0.15.tar.gz';
//
// PRODUCTION URL (self-hosted on tarot.mpmagic.co.uk — uncomment when the file is uploaded):
const VOSK_MODEL_URL = 'https://tarot.mpmagic.co.uk/assets/vosk-model-small-en-us-0.15.tar.gz';
const MODEL_CACHE_NAME = 'vosk-model-v1';

/**
 * Check if the Vosk model is already cached in CacheStorage.
 */
export async function isModelCached(): Promise<boolean> {
  if (typeof window === 'undefined' || !('caches' in window)) return false;
  try {
    const cache = await caches.open(MODEL_CACHE_NAME);
    const match = await cache.match(VOSK_MODEL_URL);
    return !!match;
  } catch {
    return false;
  }
}

/**
 * Pre-load the Vosk model on app boot by sending an 'init' message to the worker.
 * The worker handles the actual download and caching.
 * This is a fire-and-forget operation — it does not block the UI.
 *
 * @param grammar - Optional grammar JSON string from session storage
 */
export function preloadVoskModel(grammar?: string | null): void {
  if (typeof window === 'undefined') return;

  // Only pre-load if model is not already cached
  isModelCached().then((cached) => {
    if (cached) return; // Already cached, nothing to do

    try {
      const worker = new Worker('/vosk-worker.js');
      worker.postMessage({ type: 'init', grammar: grammar || null });

      worker.onmessage = (e) => {
        if (e.data.type === 'ready' || e.data.type === 'error') {
          // Pre-loading complete (success or failure) — terminate worker
          worker.terminate();
        }
      };

      worker.onerror = () => {
        worker.terminate();
      };
    } catch {
      // Worker creation failed — ignore, will be retried on first use
    }
  });
}
